/**
 * Prints the "Get it by <date>" estimate on the product page.
 *
 * The date is lead days ahead of today in the store's timezone, so it has to be
 * worked out in the browser: a Liquid-rendered date would be baked into the
 * cached page and go stale. The element ships hidden and reveals itself only
 * once a date is in place, so with JavaScript off the line never appears.
 *
 * Markup: blocks/bettino-delivery-exchange.liquid.
 *
 * The line also stays hidden while the selected variant is unavailable
 * (`data-unavailable`). A size change morphs only the variant picker, so, like
 * bettino-notify-me.js, this listens for the product select on its section and
 * copies the flag from the re-rendered section.
 */
import { StandardEvents } from '@shopify/events';

class BettinoDeliveryDate extends HTMLElement {
  connectedCallback() {
    this.section = this.closest('.shopify-section, dialog');
    this.section?.addEventListener(StandardEvents.productSelect, this.#handleProductSelect);
    this.#render();
  }

  disconnectedCallback() {
    this.section?.removeEventListener(StandardEvents.productSelect, this.#handleProductSelect);
  }

  #handleProductSelect = (event) => {
    if (!(event.target instanceof Element) || event.target.closest('product-card')) return;

    event.promise
      .then(({ detail }) => {
        if (!detail?.html) return;
        if (!detail.newProduct && detail.productId && detail.productId !== this.dataset.productId) return;

        const next = detail.html.querySelector('bettino-delivery-date');
        if (!next) return;

        this.toggleAttribute('data-unavailable', next.hasAttribute('data-unavailable'));
        this.#render();
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[bettino-delivery-date] Event promise rejected:', error);
      });
  };

  get #timezone() {
    return this.getAttribute('timezone') || 'Asia/Kolkata';
  }

  get #leadDays() {
    const days = Number.parseInt(this.getAttribute('lead-days') ?? '', 10);
    return Number.isFinite(days) ? days : 0;
  }

  /** Today in the store's timezone, as a UTC-based date with no time part. */
  #todayInStoreTimezone() {
    const parts = new Intl.DateTimeFormat('en-CA', {
      timeZone: this.#timezone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    }).formatToParts(new Date());

    const value = (type) => Number(parts.find((part) => part.type === type)?.value);

    return { year: value('year'), month: value('month'), day: value('day') };
  }

  /**
   * "Ddd, D Mon" (e.g. "Thu, 24 Sep"), assembled part by part: the locale
   * patterns that come closest either drop the comma or abbreviate September
   * as "Sept".
   */
  #format(date) {
    const parts = new Intl.DateTimeFormat('en-US', {
      timeZone: 'UTC',
      weekday: 'short',
      day: 'numeric',
      month: 'short',
    }).formatToParts(date);

    const value = (type) => parts.find((part) => part.type === type)?.value;

    return `${value('weekday')}, ${value('day')} ${value('month')}`;
  }

  #render() {
    const output = this.querySelector('[data-delivery-date]');
    if (!output) return;

    if (this.hasAttribute('data-unavailable')) {
      this.hidden = true;
      return;
    }

    let date;
    try {
      const today = this.#todayInStoreTimezone();
      date = new Date(Date.UTC(today.year, today.month - 1, today.day + this.#leadDays));
    } catch {
      // An unknown timezone throws in Intl; leaving the line hidden is the
      // same outcome as JavaScript being off.
      return;
    }

    if (Number.isNaN(date.getTime())) return;

    output.textContent = this.#format(date);
    output.setAttribute('datetime', date.toISOString().slice(0, 10));
    this.hidden = false;
  }
}

if (!customElements.get('bettino-delivery-date')) {
  customElements.define('bettino-delivery-date', BettinoDeliveryDate);
}
